import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Reusable communication tracking for any admin entity.
 * Use `entityType` values like:
 *   - "popup_vendor_confirmation"
 *   - "popup_early_bird_ticket"
 *   - "class_registration_confirmation"
 *   - "field_trip_confirmation"
 *   - "volunteer_confirmation"
 *   - "instructor_onboarding"
 */

export type CommStatus = "pending" | "sent";

export interface CommunicationStatusRow {
  id: string;
  entity_type: string;
  entity_id: string;
  channel: string;
  status: CommStatus;
  sent_at: string | null;
  sent_by_user_id: string | null;
  sent_by_name: string | null;
  notes: string | null;
  updated_at: string;
}

export interface CommunicationLogRow {
  id: string;
  entity_type: string;
  entity_id: string;
  channel: string;
  previous_status: string | null;
  new_status: string;
  changed_by_user_id: string | null;
  changed_by_name: string | null;
  note: string | null;
  changed_at: string;
}

async function getAdminDisplayName(userId: string | undefined, email: string | undefined) {
  if (!userId) return "System";
  const { data } = await supabase
    .from("profiles")
    .select("display_name")
    .eq("user_id", userId)
    .maybeSingle();
  return data?.display_name || email || "Administrator";
}

/**
 * Fetch statuses for a whole entity_type in one query. Returns a map keyed by
 * entity_id → status row. Includes realtime subscription so the UI stays live
 * when other admins act.
 */
export function useCommunicationStatusList(entityType: string, channel = "email") {
  const [rows, setRows] = useState<Record<string, CommunicationStatusRow>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from("communication_status")
      .select("*")
      .eq("entity_type", entityType)
      .eq("channel", channel);
    const map: Record<string, CommunicationStatusRow> = {};
    (data || []).forEach((r: any) => (map[r.entity_id] = r));
    setRows(map);
    setLoading(false);
  }, [entityType, channel]);

  useEffect(() => {
    load();
    const ch = supabase
      .channel(`comm-status-${entityType}-${channel}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "communication_status",
          filter: `entity_type=eq.${entityType}`,
        },
        (payload) => {
          setRows((prev) => {
            const next = { ...prev };
            if (payload.eventType === "DELETE") {
              const old = payload.old as CommunicationStatusRow;
              delete next[old.entity_id];
            } else {
              const row = payload.new as CommunicationStatusRow;
              if (row.channel === channel) next[row.entity_id] = row;
            }
            return next;
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [entityType, channel, load]);

  return { rows, loading, reload: load };
}

/**
 * Set the status for a single entity. Records an audit log entry.
 * Designed so future automated flows can also call it (pass systemActor=true).
 */
export function useSetCommunicationStatus() {
  const { user } = useAuth();

  const setStatus = useCallback(
    async (
      entityType: string,
      entityId: string,
      newStatus: CommStatus,
      opts?: { channel?: string; note?: string; systemActor?: boolean },
    ) => {
      const channel = opts?.channel ?? "email";
      const actorId = opts?.systemActor ? null : user?.id ?? null;
      const actorName = opts?.systemActor
        ? "System"
        : await getAdminDisplayName(user?.id, user?.email);

      // Read current status for the log
      const { data: existing } = await supabase
        .from("communication_status")
        .select("*")
        .eq("entity_type", entityType)
        .eq("entity_id", entityId)
        .eq("channel", channel)
        .maybeSingle();

      const previousStatus = (existing?.status as CommStatus | undefined) ?? null;
      const nowIso = new Date().toISOString();

      const payload: any = {
        entity_type: entityType,
        entity_id: entityId,
        channel,
        status: newStatus,
        sent_at: newStatus === "sent" ? nowIso : null,
        sent_by_user_id: newStatus === "sent" ? actorId : null,
        sent_by_name: newStatus === "sent" ? actorName : null,
      };
      if (opts?.note !== undefined) payload.notes = opts.note;

      const { error } = await supabase
        .from("communication_status")
        .upsert(payload, { onConflict: "entity_type,entity_id,channel" });
      if (error) return { error };

      await supabase.from("communication_status_log").insert({
        entity_type: entityType,
        entity_id: entityId,
        channel,
        previous_status: previousStatus,
        new_status: newStatus,
        changed_by_user_id: actorId,
        changed_by_name: actorName,
        note: opts?.note ?? null,
      });

      return { error: null };
    },
    [user],
  );

  return { setStatus };
}

export function useCommunicationLog(entityType: string, entityId: string | null, channel = "email") {
  const [entries, setEntries] = useState<CommunicationLogRow[]>([]);
  useEffect(() => {
    if (!entityId) return;
    supabase
      .from("communication_status_log")
      .select("*")
      .eq("entity_type", entityType)
      .eq("entity_id", entityId)
      .eq("channel", channel)
      .order("changed_at", { ascending: false })
      .then(({ data }) => setEntries((data as CommunicationLogRow[]) || []));
  }, [entityType, entityId, channel]);
  return entries;
}
